import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "../../utils/i18n/i18n";
import { STRING_LANGUAGE_COMPRARTODO } from "../../utils/const/string-const";
import { EC } from "../../utils/const/ecommerce-colors-const";

const LANGUAGES = [
    { value: "en", label: "English", flag: "🇺🇸" },
    { value: "es", label: "Español", flag: "🇪🇸" },
    { value: "pt", label: "Português", flag: "🇧🇷" },
];

export default function AuthPageShell({ badge, badgeIcon = "🔐", title, subtitle, children, maxWidth = "max-w-md" }) {
    const history = useHistory();
    const { t } = useTranslation();
    const [selectedLanguage, setSelectedLanguage] = useState("en");
    const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const currentYear = new Date().getFullYear();

    useEffect(() => {
        const language = localStorage.getItem(STRING_LANGUAGE_COMPRARTODO);
        if (language) {
            setSelectedLanguage(language);
            i18n.changeLanguage(language);
        }
    }, []);

    useEffect(() => {
        if (!isLanguageDropdownOpen) return undefined;
        const handleClickOutside = (event) => {
            if (!event.target.closest(".language-dropdown")) {
                setIsLanguageDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isLanguageDropdownOpen]);

    return (
        <div className={EC.page.root}>
            <div className="pointer-events-none fixed inset-0 overflow-hidden" aria-hidden>
                <div className={`absolute -top-40 -right-32 h-96 w-96 rounded-full blur-3xl ${EC.page.blobPrimary}`} />
                <div className={`absolute top-1/3 -left-24 h-80 w-80 rounded-full blur-3xl ${EC.page.blobPrice}`} />
            </div>

            <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/90 backdrop-blur-md shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center py-3.5 gap-3">
                        <button
                            type="button"
                            onClick={() => history.push("/landing")}
                            className={`flex items-center gap-2 text-left text-xl sm:text-2xl tracking-tight transition shrink-0 ${EC.brand.textLg}`}
                        >
                            <span className="text-2xl shrink-0" aria-hidden>🛒</span>
                            <span className="truncate max-w-[9rem] xs:max-w-none">{t("ct_brand")}</span>
                        </button>

                        <div className="hidden sm:flex items-center gap-4 text-sm">
                            <button type="button" onClick={() => history.push("/landing")} className={EC.nav.link}>
                                {t("label_volver_al_inicio")}
                            </button>
                        </div>

                        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                            <button
                                type="button"
                                className="sm:hidden p-2 rounded-lg text-slate-700 hover:bg-slate-100 border border-slate-200"
                                onClick={() => setMobileNavOpen((o) => !o)}
                                aria-expanded={mobileNavOpen}
                                aria-label="Menu"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    {mobileNavOpen ? (
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    ) : (
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                    )}
                                </svg>
                            </button>
                            <div className="hidden sm:block">
                                <LanguageDropdown
                                    languages={LANGUAGES}
                                    selectedLanguage={selectedLanguage}
                                    setSelectedLanguage={setSelectedLanguage}
                                    isOpen={isLanguageDropdownOpen}
                                    setIsOpen={setIsLanguageDropdownOpen}
                                />
                            </div>
                        </div>
                    </div>

                    {mobileNavOpen && (
                        <div className="sm:hidden border-t border-slate-100 py-3 flex flex-col gap-2 px-1">
                            <button
                                type="button"
                                onClick={() => {
                                    history.push("/landing");
                                    setMobileNavOpen(false);
                                }}
                                className="text-left px-3 py-2 rounded-lg text-slate-700 hover:bg-ct-primary-50 font-medium"
                            >
                                {t("label_volver_al_inicio")}
                            </button>
                        </div>
                    )}

                    <div className="sm:hidden border-t border-slate-100 py-2 flex justify-center">
                        <LanguageDropdown
                            languages={LANGUAGES}
                            selectedLanguage={selectedLanguage}
                            setSelectedLanguage={setSelectedLanguage}
                            isOpen={isLanguageDropdownOpen}
                            setIsOpen={setIsLanguageDropdownOpen}
                        />
                    </div>
                </div>
            </header>

            <main className="relative px-4 sm:px-6 lg:px-8 pt-10 pb-16 sm:pt-14 sm:pb-24 flex flex-col items-center">
                <div className={`w-full ${maxWidth}`}>
                    {badge && (
                        <span className={`inline-flex items-center gap-2 mb-4 px-4 py-1.5 rounded-full text-sm font-semibold ${EC.hero.badge}`}>
                            <span aria-hidden>{badgeIcon}</span>
                            {badge}
                        </span>
                    )}
                    {title && (
                        <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight leading-tight mb-3">
                            {title}
                        </h1>
                    )}
                    {subtitle && (
                        <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-8">{subtitle}</p>
                    )}
                    {children}
                </div>
            </main>

            <footer className="relative bg-slate-950 text-slate-300 py-8 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto text-center">
                    <p className="text-white font-bold text-lg flex items-center justify-center gap-2">
                        <span aria-hidden>🛒</span> {t("ct_brand")}
                    </p>
                    <p className="text-sm text-slate-400 mt-2">{t("ct_footer_line")}</p>
                    <p className="text-sm text-slate-500 mt-6">
                        © {currentYear} {t("ct_brand")}. {t("te_footer_rights")}
                    </p>
                </div>
            </footer>
        </div>
    );
}

function LanguageDropdown({ languages, selectedLanguage, setSelectedLanguage, isOpen, setIsOpen }) {
    return (
        <div className="relative language-dropdown">
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition border border-transparent ${EC.nav.langHover}`}
                aria-expanded={isOpen}
            >
                <span className="text-xl">{languages.find((l) => l.value === selectedLanguage)?.flag}</span>
                <span className="text-slate-700 text-sm font-medium hidden md:inline">
                    {languages.find((l) => l.value === selectedLanguage)?.label}
                </span>
                <svg className={`w-4 h-4 text-slate-500 transition-transform ${isOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>
            {isOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-slate-200 py-2 z-50">
                    {languages.map((language) => (
                        <button
                            type="button"
                            key={language.value}
                            onClick={() => {
                                setSelectedLanguage(language.value);
                                setIsOpen(false);
                                i18n.changeLanguage(language.value);
                                localStorage.setItem(STRING_LANGUAGE_COMPRARTODO, language.value);
                            }}
                            className={`w-full flex items-center space-x-3 px-4 py-2 transition text-left hover:bg-ct-primary-50 ${selectedLanguage === language.value ? EC.nav.langActive : "text-slate-700"
                                }`}
                        >
                            <span className="text-xl">{language.flag}</span>
                            <span className="font-medium">{language.label}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

export function AuthFormCard({ children }) {
    return (
        <div className={EC.form.card}>
            <div className={EC.form.cardGlow} aria-hidden />
            <div className="relative">{children}</div>
        </div>
    );
}
